import Select from 'components/Common/Form/Input/Select'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading, { Heading5Style } from 'LuxKit/Typography/Heading'
import { formToObject } from 'lib/forms/formToObject'
import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import ExperienceDetailsTransferBookingTravellers from './ExperienceTransferBookingTravellers'
import ExperienceTransferBookingOption from './ExperienceTransferBookingOption'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import ExperienceTransferBookingForm from './ExperienceTransferBookingForm'
import useToggle from 'hooks/useToggle'
import type { TransferForm } from './ExperienceTransferUtils'
import { findBestTransfer } from './ExperienceTransferUtils'
import noop from 'lib/function/noop'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ExperienceTransferBookingOptionError from './ExperienceTransferBookingOptionError'
import ExperienceDetailsTransferBookingBaggage from './ExperienceTransferBookingBaggage'
import Accordion from 'LuxKit/Accordion'

const AccordionHeadingOverride = styled.div`
  button > div:first-child div[class*='Typography'] {
    ${Heading5Style}
  }
`

const defaultAirportView: App.ExperienceTransferView = {
  type: 'HOTEL-TO-AIRPORT',
  hotel: {},
  travellers: {
    adults: 2,
    children: 0,
    childSeats: 0,
  },
  baggage: {
    bags: 0,
    oversizedBags: 0,
  },
}

const defaultHotelView: App.ExperienceTransferView = {
  type: 'AIRPORT-TO-HOTEL',
  hotel: {},
  travellers: {
    adults: 2,
    children: 0,
    childSeats: 0,
  },
  baggage: {
    bags: 0,
    oversizedBags: 0,
  },
}

type TransferType = App.ExperienceTransferType | 'ROUND-TRIP'

interface Props {
  defaultToAirportTransfer?: App.ExperienceTransferView
  defaultToHotelTransfer?: App.ExperienceTransferView
  preferredTransferType?: TransferType
  transferOptions: Array<App.ExperienceTransferOption>
  experience: App.ExperienceBookingView
  hasLoyaltyBenefit?: boolean
  formId?: string
  onPriceChange?: (total: number, totalMember: number, totalGrossPrice: number, isFromPrice?: boolean) => void
  onSubmit?: (transfers: Array<App.ExperienceTransferView>) => void
}

function ExperienceTransferMultipleBooking(props: Props) {
  const {
    defaultToAirportTransfer,
    defaultToHotelTransfer,
    preferredTransferType,
    transferOptions,
    experience,
    hasLoyaltyBenefit,
    formId,
    onPriceChange = noop,
    onSubmit = noop,
  } = props

  const intl = useIntl()

  const transferTypeLabels = useMemo(() => new Map<TransferType, string>([
    ['AIRPORT-TO-HOTEL', intl.formatMessage({ id: 'UYwhvmxUQHa_gBqVfUQ0', defaultMessage: 'Airport to hotel transfer only', description: 'Transfer type option label for airport to hotel one-way transfer' })],
    ['HOTEL-TO-AIRPORT', intl.formatMessage({ id: '4jJPqVHqNBwU6XMkp49I', defaultMessage: 'Hotel to airport transfer only', description: 'Transfer type option label for hotel to airport one-way transfer' })],
    ['ROUND-TRIP', intl.formatMessage({ id: 'rHZDffCm1nS6bTZm1IL4', defaultMessage: 'Round trip transfer', description: 'Transfer type option label for round trip transfer' })],
  ]), [intl])

  const [toHotelOptions, toAirportOptions] = useMemo(() => ([
    transferOptions.filter(option => option.transferTypes.includes('AIRPORT-TO-HOTEL')),
    transferOptions.filter(option => option.transferTypes.includes('HOTEL-TO-AIRPORT')),
  ]), [transferOptions])

  const transferTypes = useMemo((): Array<TransferType> => ([
    toHotelOptions.length > 0 && toAirportOptions.length > 0 ? 'ROUND-TRIP' : undefined,
    toHotelOptions.length > 0 ? 'AIRPORT-TO-HOTEL' : undefined,
    toAirportOptions.length > 0 ? 'HOTEL-TO-AIRPORT' : undefined,
  ].filter(Boolean) as Array<TransferType>), [toHotelOptions, toAirportOptions])

  const initialTransferType = useMemo((): TransferType => {
    if (defaultToHotelTransfer && defaultToAirportTransfer) {
      return 'ROUND-TRIP'
    } else if (defaultToHotelTransfer) {
      return 'AIRPORT-TO-HOTEL'
    } else if (defaultToAirportTransfer) {
      return 'HOTEL-TO-AIRPORT'
    }

    if (preferredTransferType && transferTypes.includes(preferredTransferType)) {
      return preferredTransferType
    }

    return transferTypes[0] ?? 'ROUND-TRIP'
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [toHotel, setToHotel] = useState<App.ExperienceTransferView>(defaultToHotelTransfer ?? defaultHotelView)
  const [toAirport, setToAirport] = useState<App.ExperienceTransferView>(defaultToAirportTransfer ?? defaultAirportView)

  const [transferType, setTransferType] = useState<TransferType>(initialTransferType)
  const [sameLocation, toggleSameLocation] = useToggle(true)
  const [sameTravellers, toggleSameTravellers] = useToggle(false)
  const [sameBaggage, toggleSameBaggage] = useToggle(false)

  const isRoundTrip = transferType === 'ROUND-TRIP'
  const isToHotelTransfer = transferType === 'AIRPORT-TO-HOTEL'
  const isToAirportTransfer = transferType === 'HOTEL-TO-AIRPORT'
  const hasToHotelTransfer = isRoundTrip || isToHotelTransfer
  const hasToAirportTransfer = isRoundTrip || isToAirportTransfer
  const showDropOff = isRoundTrip && !!toHotel.date && !!toHotel.hotel

  useEffect(() => {
    if (isRoundTrip && (sameLocation || sameTravellers || sameBaggage)) {
      // we need to sync our changes to the airport object too if it's supposed to be the same
      setToAirport({
        ...toAirport,
        hotel: sameLocation ? toHotel.hotel : toAirport.hotel,
        travellers: sameTravellers ? toHotel.travellers : toAirport.travellers,
        baggage: sameBaggage ? toHotel.baggage : toAirport.baggage,
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoundTrip, sameLocation, sameTravellers, sameBaggage, toHotel])

  const hasTicketValues = useMemo(() => {
    const hotelValidity = !!(toHotel.hotel.kmFromAirport)
    const airportValidity = !!(toAirport.hotel.kmFromAirport)

    switch (transferType) {
      case 'ROUND-TRIP':
        return hotelValidity || airportValidity
      case 'AIRPORT-TO-HOTEL':
        return hotelValidity
      case 'HOTEL-TO-AIRPORT':
        return airportValidity
    }
  }, [toHotel, toAirport, transferType])

  // Getting the best option for the user
  const { bestTransfer: bestToHotelOption, noTransferReason: noBestToHotelTransferReason } = useMemo(() => {
    return findBestTransfer(toHotel, toHotelOptions)
  }, [toHotel, toHotelOptions])

  const { bestTransfer: bestToAirportOption, noTransferReason: noBestToAirportTransferReason } = useMemo(() => {
    return findBestTransfer(toAirport, toAirportOptions)
  }, [toAirport, toAirportOptions])

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formValues = formToObject<TransferForm>(e.currentTarget)

    const purchaseToHotel: App.ExperienceTransferView | undefined = formValues.toHotel ? {
      ...toHotel,
      travellers: {
        ...toHotel.travellers,
        // if we have no children, reset the seats. We only kept them around in case the user
        // flicked back and forth between having children and not for convenience
        boosterSeats: toHotel.travellers.children ? toHotel.travellers.boosterSeats : 0,
        childSeats: toHotel.travellers.children ? toHotel.travellers.childSeats : 0,
      },
      baggage: {
        bags: toHotel.baggage?.bags ?? 0,
        oversizedBags: toHotel.baggage?.oversizedBags ?? 0,
      },
      type: formValues.toHotel.type,
      flightNumber: formValues.toHotel.flightNumber,
      time: formValues.toHotel.time,
      date: formValues.toHotel.date,
      option: bestToHotelOption,
      title: experience.title,
    } : undefined

    const purchaseToAirport: App.ExperienceTransferView | undefined = formValues.toAirport ? {
      ...toAirport,
      travellers: {
        ...toAirport.travellers,
        // if we have no children, reset the seats. We only kept them around in case the user
        // flicked back and forth between having children and not for convenience
        boosterSeats: toAirport.travellers.children && toAirport.travellers.children > 0 ? toAirport.travellers.boosterSeats : 0,
        childSeats: toAirport.travellers.children && toAirport.travellers.children > 0 ? toAirport.travellers.childSeats : 0,
      },
      baggage: {
        bags: toAirport.baggage?.bags ?? 0,
        oversizedBags: toAirport.baggage?.oversizedBags ?? 0,
      },
      type: formValues.toAirport.type,
      flightNumber: formValues.toAirport.flightNumber,
      time: formValues.toAirport.time,
      date: formValues.toAirport.date,
      option: bestToAirportOption,
      title: experience.title,
    } : undefined

    onSubmit([purchaseToHotel, purchaseToAirport].filter(Boolean))
  }

  const isFromPrice = !bestToHotelOption && !bestToAirportOption
  const [totalPrice, totalMemberPrice, totalGrossPrice] = useMemo(() => {
    if (isFromPrice) {
      return [experience.price * (isRoundTrip ? 2 : 1), experience.pricing.memberPrice * (isRoundTrip ? 2 : 1)]
    }

    const toHotelPrice = hasToHotelTransfer ? bestToHotelOption?.price ?? 0 : 0
    const toAirportPrice = hasToAirportTransfer ? bestToAirportOption?.price ?? 0 : 0
    const toHotelMemberPrice = hasToHotelTransfer ? bestToHotelOption?.memberPrice ?? 0 : 0
    const toAirportMemberPrice = hasToAirportTransfer ? bestToAirportOption?.memberPrice ?? 0 : 0
    const totalHotelGrossPrice = (toHotelPrice && bestToHotelOption?.discounts && bestToHotelOption.discounts.base.amount > 0) ? toHotelPrice + bestToHotelOption.discounts.base.amount : toHotelPrice
    const totalAirportGrossPrice = (toAirportPrice && bestToAirportOption?.discounts && bestToAirportOption.discounts.base.amount > 0) ? toAirportPrice + bestToAirportOption.discounts.base.amount : toAirportPrice

    return [toHotelPrice + toAirportPrice, toHotelMemberPrice + toAirportMemberPrice, totalHotelGrossPrice + totalAirportGrossPrice]
  }, [
    bestToAirportOption?.price,
    bestToAirportOption?.memberPrice,
    bestToHotelOption?.price,
    bestToHotelOption?.memberPrice,
    experience.price,
    experience.pricing.memberPrice,
    hasToAirportTransfer,
    hasToHotelTransfer,
    isFromPrice,
    isRoundTrip,
    bestToAirportOption?.discounts,
    bestToHotelOption?.discounts,
  ])

  useEffect(() => {
    onPriceChange(totalPrice, totalMemberPrice, totalGrossPrice, isFromPrice)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPrice, totalMemberPrice, totalGrossPrice, isFromPrice])

  const selectedTransfersLabel = intl.formatMessage({
    id: 'J-nZmr4Sdlco2wv1I7iX',
    defaultMessage: 'Your selected airport transfers:',
    description: 'Heading for selected round trip airport transfers summary',
  })
  const selectedTransferLabel = intl.formatMessage({
    id: '-Sykeo1CKS6UQWNta7F5',
    defaultMessage: 'Your selected airport transfer:',
    description: 'Heading for selected one-way airport transfer summary',
  })

  return <VerticalSpacer gap={24} as="form" onSubmit={onFormSubmit} id={formId}>
    <VerticalSpacer gap={16}>
      <Select
        label={intl.formatMessage({
          id: 'dNUc2HQg_xXVrphH3lUF',
          defaultMessage: 'Transfer type',
          description: 'Form field label for the transfer type dropdown selector',
        })}
        value={transferType}
        onChange={(e) => setTransferType(e.currentTarget.value as TransferType)}
        required
      >
        {transferTypes.map(type =>
          <option key={type} value={type}>{transferTypeLabels.get(type)}</option>,
        )}
      </Select>
      {hasToHotelTransfer && <VerticalSpacer gap={16} hidden={!hasToHotelTransfer}>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: '6QpOHoqovwIX4pU7nZGO',
            defaultMessage: 'Airport pick up',
            description: 'Heading for airport pick up transfer booking section',
          })}
        </Heading>
        <ExperienceTransferBookingForm
          transfer={toHotel}
          name="toHotel"
          onChange={setToHotel}
          experience={experience.originalExperience}
        />
        <ExperienceDetailsTransferBookingTravellers
          name="toHotel"
          transfer={toHotel}
          onChange={setToHotel}
          options={toHotelOptions}
        />
        <ExperienceDetailsTransferBookingBaggage
          name="toHotel"
          transfer={toHotel}
          onChange={setToHotel}
          options={toHotelOptions}
        />
      </VerticalSpacer>}
      {isRoundTrip && <Divider kind="primary" />}
      {isRoundTrip && <AccordionHeadingOverride>
        <Accordion
          size="large"
          expanded={showDropOff}
          hideDivider
          heading={intl.formatMessage({
            id: 'cDS4raVZ8bWi33gj6jLG',
            defaultMessage: 'Hotel Transfer',
            description: 'Accordion heading for hotel transfer section in round trip booking',
          })}
        >
          <VerticalSpacer gap={16}>
            <CheckboxInput size="large" checked={sameLocation} onChange={toggleSameLocation}>
              {intl.formatMessage({
                id: 'XcflzgeEPt344ymPXW90',
                defaultMessage: 'Locations same as airport pick up',
                description: 'Checkbox label to reuse airport pick up location for hotel transfer',
              })}
            </CheckboxInput>
            <ExperienceTransferBookingForm
              transfer={toAirport}
              name="toAirport"
              disableLocations={sameLocation}
              onChange={setToAirport}
              experience={experience.originalExperience}
            />
            <CheckboxInput size="large" checked={sameTravellers} onChange={toggleSameTravellers}>
              {intl.formatMessage({
                id: 'qMn9SHLXL9KYsZSISHBc',
                defaultMessage: 'Travellers same as airport pick up',
                description: 'Checkbox label to reuse airport pick up traveller details for hotel transfer',
              })}
            </CheckboxInput>
            <CheckboxInput size="large" checked={sameBaggage} onChange={toggleSameBaggage}>
              {intl.formatMessage({
                id: 'nJ79WxMcVPc334wvXjDl',
                defaultMessage: 'Baggage same as airport pick up',
                description: 'Checkbox label to reuse airport pick up baggage details for hotel transfer',
              })}
            </CheckboxInput>
            {!sameTravellers && <ExperienceDetailsTransferBookingTravellers
              name="toAirport"
              transfer={toAirport}
              onChange={setToAirport}
              options={toAirportOptions}
            />}
            {!sameBaggage && <ExperienceDetailsTransferBookingBaggage
              name="toAirport"
              transfer={toAirport}
              onChange={setToAirport}
              options={toAirportOptions}
            />}
          </VerticalSpacer>
        </Accordion>
      </AccordionHeadingOverride>}
      {isToAirportTransfer && !isRoundTrip && <VerticalSpacer gap={16}>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'xIp4aDDzYLucxOLVMD_t',
            defaultMessage: 'Hotel Transfer',
            description: 'Heading for hotel transfer booking section in the booking flow',
          })}
        </Heading>
        {isRoundTrip && <CheckboxInput size="large" checked={sameLocation} onChange={toggleSameLocation}>
          {intl.formatMessage({
            id: 'XcflzgeEPt344ymPXW90',
            defaultMessage: 'Locations same as airport pick up',
            description: 'Checkbox label to reuse airport pick up location for hotel transfer',
          })}
        </CheckboxInput>}
        <ExperienceTransferBookingForm
          transfer={toAirport}
          name="toAirport"
          disableLocations={sameLocation && isRoundTrip}
          onChange={setToAirport}
          experience={experience.originalExperience}
        />
        {isRoundTrip && <CheckboxInput size="large" checked={sameTravellers} onChange={toggleSameTravellers}>
          {intl.formatMessage({
            id: 'qMn9SHLXL9KYsZSISHBc',
            defaultMessage: 'Travellers same as airport pick up',
            description: 'Checkbox label to reuse airport pick up traveller details for hotel transfer',
          })}
        </CheckboxInput>}
        {isRoundTrip && <CheckboxInput size="large" checked={sameBaggage} onChange={toggleSameBaggage}>
          {intl.formatMessage({
            id: 'nJ79WxMcVPc334wvXjDl',
            defaultMessage: 'Baggage same as airport pick up',
            description: 'Checkbox label to reuse airport pick up baggage details for hotel transfer',
          })}
        </CheckboxInput>}
        {(!isRoundTrip || !sameTravellers) && <ExperienceDetailsTransferBookingTravellers
          name="toAirport"
          transfer={toAirport}
          onChange={setToAirport}
          options={toAirportOptions}
        />}
        {(!isRoundTrip || !sameBaggage) && <ExperienceDetailsTransferBookingBaggage
          name="toAirport"
          transfer={toAirport}
          onChange={setToAirport}
          options={toAirportOptions}
        />}
      </VerticalSpacer>}
    </VerticalSpacer>
    {hasTicketValues && <>
      <Divider kind="primary" />
      <VerticalSpacer gap={12} style={{ position: 'relative' }}>
        <BodyText variant="large" weight="bold">{isRoundTrip ? selectedTransfersLabel : selectedTransferLabel}</BodyText>
        {hasToHotelTransfer && <>
          {bestToHotelOption && <ExperienceTransferBookingOption transferTicket={bestToHotelOption} hasLoyaltyBenefit={hasLoyaltyBenefit} />}
          {noBestToHotelTransferReason && <ExperienceTransferBookingOptionError reason={noBestToHotelTransferReason} />}
        </> }
        {isRoundTrip && <Divider kind="primary" hidden={!showDropOff} />}
        {(isRoundTrip ? showDropOff : isToAirportTransfer) && <div>
          {bestToAirportOption && <ExperienceTransferBookingOption transferTicket={bestToAirportOption} hasLoyaltyBenefit={hasLoyaltyBenefit} />}
          {noBestToAirportTransferReason && <ExperienceTransferBookingOptionError reason={noBestToAirportTransferReason} />}
        </div>}
        <HiddenInput
          required={(hasToHotelTransfer && !bestToHotelOption) || (hasToAirportTransfer && !bestToAirportOption)}
          invalidMessage={intl.formatMessage({
            id: 'MyZ-tyNXZ8WhR3zm3S00',
            defaultMessage: 'Please update your transfer details and try again.',
            description: 'Error message when transfer details are invalid or incomplete',
          })}
        />
      </VerticalSpacer>
    </>}
  </VerticalSpacer>
}

export default ExperienceTransferMultipleBooking
